import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { cameraErrorMessage, startCamera as openCamera } from './camera';
import { createDeduper } from './dedupe';
import { createWedge } from './wedge';

export interface Scanner {
  videoRef: RefObject<HTMLVideoElement | null>;
  cameraOn: boolean;
  cameraError: string | null;
  startCamera(): void;
  stopCamera(): void;
  submitManual(code: string): void;
}

/**
 * The three ways a code reaches this app — the camera, a Bluetooth/USB wedge scanner and the
 * worker's own thumbs — funnelled through one deduper so the same sticker cannot be counted
 * twice. The camera is the fast path, not the only one: when it is blocked, missing or
 * unsupported the other two keep working and `cameraError` says what to do instead.
 */
export function useScanner({ onCode, enabled }: { onCode: (raw: string) => void; enabled: boolean }): Scanner {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  /** The worker's own on/off choice; the camera starts on so a dock scan needs no taps. */
  const [cameraWanted, setCameraWanted] = useState(true);
  /** Bumped by every "Try again", so a retry after a failure actually re-runs the effect. */
  const [attempt, setAttempt] = useState(0);
  /** Backgrounded: the tab is hidden, or the page is being frozen or put in the back/forward cache. */
  const [pageHidden, setPageHidden] = useState(() => typeof document !== 'undefined' && document.hidden);

  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;
  const dedupeRef = useRef<(code: string) => boolean>(undefined);
  dedupeRef.current ??= createDeduper(2000);

  const emit = useCallback((raw: string) => {
    const code = raw.trim();
    if (!code || !dedupeRef.current?.(code)) return;
    onCodeRef.current(code);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const onKey = createWedge(emit);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, emit]);

  /**
   * A backgrounded PWA must not keep the track live: that is the phone's camera indicator staying
   * lit while the worker is in another app (the privacy complaint) and the 120 ms decode loop
   * running on a screen nobody is looking at (the battery one). The worker's own on/off choice is
   * untouched, so coming back to the screen brings the camera back exactly as they left it.
   */
  useEffect(() => {
    const sync = () => setPageHidden(document.hidden);
    const hide = () => setPageHidden(true);
    document.addEventListener('visibilitychange', sync);
    document.addEventListener('freeze', hide);
    document.addEventListener('resume', sync);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      document.removeEventListener('freeze', hide);
      document.removeEventListener('resume', sync);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', sync);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!enabled || !cameraWanted || pageHidden || !video) return;
    let cancelled = false;
    let stop: (() => void) | null = null;
    setCameraError(null);
    const fail = (err: unknown) => {
      if (cancelled) return;
      setCameraOn(false);
      setCameraError(cameraErrorMessage(err));
    };
    // The third argument is the failure that only shows up once frames are flowing — a code reader
    // that cannot decode anything. It arrives with the camera already released, so it lands on the
    // same `cameraError` path as a camera that never opened, and gets the same "Try again".
    void openCamera(video, emit, fail).then((release) => {
      if (cancelled) {
        release();
        return;
      }
      stop = release;
      setCameraOn(true);
    }, fail);
    return () => {
      cancelled = true;
      stop?.();
      setCameraOn(false);
    };
  }, [enabled, cameraWanted, pageHidden, attempt, emit]);

  return {
    videoRef,
    cameraOn,
    cameraError,
    startCamera: useCallback(() => {
      setCameraError(null);
      setCameraWanted(true);
      setAttempt((n) => n + 1);
    }, []),
    stopCamera: useCallback(() => setCameraWanted(false), []),
    submitManual: emit,
  };
}
